import { useState, Fragment } from "react";
import { C, Badge, EmptyState, thStyle, tdStyle, theadRowStyle } from "./ui.jsx";
import { statusMeta } from "../constants.js";
import RowActions from "./RowActions.jsx";

function relTime(s) {
  if (!s) return "—";
  const mins = Math.floor((Date.now() - new Date(s).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatusBadge({ status }) {
  const m = statusMeta(status);
  return <Badge label={m.label} bg={m.bg} color={m.color} />;
}

/* Today's queue. Rows expand in place (no new drawer pattern) to show the
 * fingerprint summary, previous-sent context, event timeline, and dry-run jobs. */
export default function QueueTable({ rows, api, allowed, reload, onNotice }) {
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({}); // runId -> { events, jobs, loading, error }

  if (rows.length === 0) {
    return <EmptyState title="No birthdays today" sub="No active employees have a birthday today (Africa/Cairo)." />;
  }

  async function fetchDetail(runId) {
    setDetail((d) => ({ ...d, [runId]: { ...(d[runId] || {}), loading: true } }));
    try {
      const [events, jobs] = await Promise.all([api.getRunEvents(runId), api.getRunJobs(runId)]);
      setDetail((d) => ({ ...d, [runId]: { loading: false, events, jobs } }));
    } catch (e) {
      setDetail((d) => ({ ...d, [runId]: { loading: false, error: e.message } }));
    }
  }

  async function toggle(row) {
    const run = row.run;
    if (expanded === row.id) { setExpanded(null); return; }
    setExpanded(row.id);
    if (!run || detail[run.id]) return;
    await fetchDetail(run.id);
  }

  const cols = ["Employee", "Emp ID", "Department", "Birthday", "Status", "Duplicate", "Photo", "Template", "Updated"];

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr style={theadRowStyle}>{cols.map((c) => <th key={c} style={thStyle}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => {
            const run = r.run;
            const dup = run?.previous_employee_year_sent ? "Prev sent" : run?.duplicate_of_run_id ? "Linked" : "—";
            const photo = run ? (run.photo_version_id ? "Has photo" : "No photo") : "—";
            const tpl = run?.template_id ? `v${run.template_version ?? "?"}` : "—";
            const isOpen = expanded === r.id;
            return (
              <Fragment key={r.id}>
                <tr onClick={() => toggle(r)}
                  style={{ cursor: "pointer", background: isOpen ? C.accentL : "transparent" }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{r.name}</td>
                  <td style={tdStyle}>{r.employee_id}</td>
                  <td style={tdStyle}>{r.department || "—"}</td>
                  <td style={tdStyle}>{r.birthday_date || r.birthday_month_day || "—"}</td>
                  <td style={tdStyle}><StatusBadge status={r.status} /></td>
                  <td style={{ ...tdStyle, color: dup === "Prev sent" ? "#B7791F" : C.sub }}>{dup}</td>
                  <td style={{ ...tdStyle, color: photo === "No photo" ? "#E65100" : C.sub }}>{photo}</td>
                  <td style={tdStyle}>{tpl}</td>
                  <td style={{ ...tdStyle, color: C.muted }}>{run ? relTime(run.updated_at || run.created_at) : "—"}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={cols.length} style={{ padding: 0, borderBottom: `1px solid ${C.border}`, background: "#FBFAF8" }}>
                      <RunDetail
                        row={r}
                        detail={run ? detail[run.id] : null}
                        api={api}
                        allowed={allowed}
                        reload={reload}
                        reloadDetail={fetchDetail}
                        onNotice={onNotice}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunDetail({ row, detail, api, allowed, reload, reloadDetail, onNotice }) {
  const run = row.run;
  if (!run) {
    return <div style={{ padding: 16, fontSize: 12.5, color: C.muted }}>No birthday run exists yet for this employee (candidate sync has not created one).</div>;
  }
  return (
    <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div>
        <DetailRow label="Fingerprint" value={run.fingerprint ? run.fingerprint.slice(0, 16) + "…" : "—"} mono />
        <DetailRow label="Previous sent" value={run.previous_employee_year_sent ? "Yes (different fingerprint)" : "No"} />
        <DetailRow label="Duplicate of" value={run.duplicate_of_run_id ? run.duplicate_of_run_id.slice(0, 8) + "…" : "—"} mono />
        <DetailRow label="Created" value={new Date(run.created_at).toLocaleString()} />
        <div style={{ marginTop: 14 }}>
          <RowActions
            run={run}
            name={row.name}
            api={api}
            allowed={allowed}
            reload={reload}
            reloadDetail={reloadDetail}
            onNotice={onNotice}
          />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Timeline & jobs</div>
        {detail?.loading && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}
        {detail?.error && <div style={{ fontSize: 12, color: C.error }}>{detail.error}</div>}
        {detail && !detail.loading && !detail.error && (
          <>
            {(detail.events || []).length === 0 && (detail.jobs || []).length === 0 && (
              <div style={{ fontSize: 12, color: C.muted }}>No events or jobs yet.</div>
            )}
            {(detail.events || []).map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: C.sub, padding: "2px 0" }}>
                <span style={{ fontWeight: 600 }}>{e.event_type}</span>
                {e.previous_status && <span style={{ color: C.muted }}> · {e.previous_status} → {e.next_status}</span>}
                <span style={{ color: C.muted }}> · {relTime(e.created_at)}</span>
              </div>
            ))}
            {(detail.jobs || []).map((j) => (
              <div key={j.id} style={{ fontSize: 12, color: C.sub, padding: "2px 0" }}>
                <span style={{ fontWeight: 600 }}>job:{j.job_type}</span>
                <span style={{ color: C.muted }}> · {j.status} · {relTime(j.created_at)}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 12.5 }}>
      <span style={{ color: C.muted, width: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ color: C.text, fontFamily: mono ? "ui-monospace,SFMono-Regular,Menlo,monospace" : "inherit" }}>{value}</span>
    </div>
  );
}
